import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import type Event from "@/types/Event";
import type IntegrationTier from "@/types/IntegrationTier";
import type TicketType from "@/types/TicketType";
import { useIntegrationAuth } from "@/composables/useIntegrationAuth";
import { useSnackbar } from "@/stores/snackbar";
import { useUserStore } from "@/stores/user";

export type TicketAccessState = "unrestricted" | "not_linked" | "plan_mismatch" | "misconfigured" | "eligible";

/** Whether the buyer may buy a type sold only to members of a plan ("Venda exclusiva"), and how to tell them why not. */
export function useTicketAccess(ticket: MaybeRefOrGetter<TicketType>, event: MaybeRefOrGetter<Event>): {
  accessState: ComputedRef<TicketAccessState>;
  /** The buyer can't buy the type yet: it shows as "Venda exclusiva" instead of an amount. */
  exclusive: ComputedRef<boolean>;
  onAccessButtonClick: () => void;
} {
  const user = useUserStore();
  const snackbar = useSnackbar();
  const { redirectToIntegration } = useIntegrationAuth();

  // A ticket's integration_tiers can reference more than one Integration (provider), and a user's
  // own tiers span every producer/integration they've ever linked to, not just this event's.
  const requiredIntegrationIds = computed<string[]>(() =>
    [ ...new Set(toValue(ticket).integration_tiers.map(tier => tier.integration.id)) ],
  );

  const hasMatchingTier = computed<boolean>(() =>
    toValue(ticket).integration_tiers.some(tier =>
      (user.data.integration_tiers ?? []).some(userTier => userTier.id === tier.id),
    ),
  );

  const userTiersForRequiredIntegrations = computed<IntegrationTier[]>(() =>
    (user.data.integration_tiers ?? []).filter(userTier => requiredIntegrationIds.value.includes(userTier.integration.id)),
  );

  const accessState = computed<TicketAccessState>(() => {
    // Tickets that aren't blocked by integration tiers are open to all users
    if (toValue(ticket).integration_tiers.length === 0) return "unrestricted";

    if (hasMatchingTier.value) return "eligible";

    if (!user.logged) return "not_linked";

    // The user has a tier for the same integration this ticket requires, just not a matching one
    if (userTiersForRequiredIntegrations.value.length > 0) return "plan_mismatch";

    // Logged in with zero tiers for this integration means their plan isn't mapped in the system.
    return "misconfigured";
  });

  const exclusive = computed<boolean>(() => ["not_linked", "plan_mismatch", "misconfigured"].includes(accessState.value));

  const memberPortalUrl = computed<string | undefined>(() => toValue(ticket).integration_tiers[0]?.integration.url || undefined);

  const misconfiguredMessage = computed<string>(() => {
    const base = "Não foi possível confirmar seu plano de sócio para este ingresso. Entre em contato com o suporte para mais informações.";
    return memberPortalUrl.value ? `${base} Área do sócio: ${memberPortalUrl.value}` : base;
  });

  const planMismatchMessage = computed<string>(() => {
    const names = [ ...new Set(toValue(ticket).integration_tiers.map(tier => tier.name)) ];
    const compatiblePlans = (new Intl.ListFormat("pt-BR", { style: "long", type: "conjunction" })).format(names);

    const base = `Este ingresso não está disponível para o seu plano. Planos compatíveis: ${compatiblePlans}.`;

    return memberPortalUrl.value ? `${base} Área do sócio: ${memberPortalUrl.value}` : base;
  });

  const accessStateMessage = computed<string | undefined>(() => {
    if (accessState.value === "misconfigured") return misconfiguredMessage.value;
    if (accessState.value === "plan_mismatch") return planMismatchMessage.value;
    return undefined;
  });

  function onAccessButtonClick(): void {
    if (accessState.value === "not_linked") {
      redirectToIntegration(toValue(ticket).integration_tiers[0].integration, toValue(event).cliente_id);
      return;
    }

    snackbar.show(accessStateMessage.value ?? "", "warning", 8000);
  }

  return { accessState, exclusive, onAccessButtonClick };
}
